import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ArrowDown, ShieldCheck, BadgeCheck, Lock,
  Hospital, Stethoscope, Users, CheckCircle2, Clock, Eye, LineChart,
  Sparkles, Layers, Compass, MapPin, Radio, Gauge,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, websiteSchema, softwareApplicationSchema } from '../lib/seoSchemas';
import { InstitutionInquiryModal } from '../components/brand/InstitutionInquiryModal';

/* ───────────────────────────────────────────────────────────────────────
   Homepage — iter 179 · Public IP / workflow protection pass.

   HARD RULE: this page does NOT reveal CaraLoom's internal workflow,
   sequencing, decision logic, orchestration model, or expansion strategy.
   No numbered sequences of internal steps. No arrows connecting internal
   capabilities. No screenshots of provider scores, ranking factors,
   routing queues, or automation rules. No "how CaraLoom works" diagrams.

   The ONLY journey visualization allowed is the patient's journey:
      HOSPITAL → CARALOOM → POST-ACUTE CARE
   MATCH · PLACE · CONNECT · MEASURE are shown as independent value
   pillars, never as sequential steps.

   Sell the outcome publicly. Demonstrate the workflow privately.
   Protect the engine completely.
   ─────────────────────────────────────────────────────────────── */

const PILLARS = [
  { key: 'match',   icon: Compass, label: 'MATCH',   body: 'Appropriate post-acute options.' },
  { key: 'place',   icon: MapPin,  label: 'PLACE',   body: 'A clearer path to confirmed placement.' },
  { key: 'connect', icon: Radio,   label: 'CONNECT', body: 'Visibility across the transition.' },
  { key: 'measure', icon: Gauge,   label: 'MEASURE', body: 'Actionable transition insight.' },
];

const HOSPITAL_ROI = [
  {
    key: 'faster_placement',
    icon: Clock,
    title: 'Faster placement',
    body: 'Reduce the time between referral and confirmed acceptance.',
  },
  {
    key: 'fewer_manual_touches',
    icon: LineChart,
    title: 'Fewer manual touches',
    body: 'Less time spent calling, faxing, searching and chasing referral status.',
  },
  {
    key: 'greater_visibility',
    icon: Eye,
    title: 'Greater visibility',
    body: 'Understand whether the patient has been accepted and when post-acute care is expected to begin.',
  },
  {
    key: 'measurable_performance',
    icon: BadgeCheck,
    title: 'Measurable performance',
    body: 'Track transition performance and placement outcomes across your service lines.',
  },
];

const TRUST_PILLARS = [
  {
    icon: ShieldCheck,
    title: 'PHI on covered paths only',
    body: 'Patient data only touches services that sit behind signed agreements. Non-covered fallbacks are refused, not silently used.',
  },
  {
    icon: Lock,
    title: 'Authorized, audited access',
    body: 'Every view of a patient record is permission-scoped and written to an immutable audit log.',
  },
  {
    icon: BadgeCheck,
    title: 'Verified providers',
    body: 'CMS Care Compare imports, license and exclusion screening, and provider verification.',
  },
  {
    icon: Layers,
    title: 'Role-scoped workflows',
    body: 'Case management, providers, and families each see the transition through their own scoped surface — never each other’s.',
  },
];

export default function LandingPage() {
  const [instModal, setInstModal] = useState({ open: false, interest: 'demo' });
  const openDemo  = () => setInstModal({ open: true, interest: 'demo' });
  const openPilot = () => setInstModal({ open: true, interest: 'pilot' });

  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  // Iter 179 · listen for ?inquiry=demo (or pilot) from the PublicNavbar
  // "Request a Demo" CTA + a custom event so the same button works on
  // in-page repeat clicks (no remount).
  useEffect(() => {
    const params = new URLSearchParams(search);
    const inquiry = params.get('inquiry');
    if (inquiry === 'demo' || inquiry === 'pilot') {
      setInstModal({ open: true, interest: inquiry });
      // Clean the URL so the modal doesn't re-open on future navigations
      // back to this route.
      params.delete('inquiry');
      const next = params.toString();
      navigate(`${pathname}${next ? `?${next}` : ''}`, { replace: true });
    }
  }, [search, pathname, navigate]);

  useEffect(() => {
    const handler = (e) => {
      const interest = (e?.detail?.interest === 'pilot') ? 'pilot' : 'demo';
      setInstModal({ open: true, interest });
    };
    window.addEventListener('caraloom:open-inquiry', handler);
    return () => window.removeEventListener('caraloom:open-inquiry', handler);
  }, []);

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text">
      <Seo
        title="CaraLoom | Post-Acute Placement & Care Transition Platform"
        description="CaraLoom is the connective layer between hospitals and post-acute care. Move patients toward appropriate placement while maintaining visibility across the transition."
        path="/"
        keywords={[
          'post-acute care transitions',
          'hospital discharge planning',
          'post-acute placement',
          'hospital referral management',
          'care transition software',
          'hospital case management software',
          'post-acute network management',
        ]}
        schema={[
          organizationSchema(),
          websiteSchema(),
          softwareApplicationSchema({
            // Deliberately outcome-oriented; no internal mechanism disclosure.
            description: 'CaraLoom is the connective layer between hospitals and post-acute care. Hospitals use CaraLoom to move patients toward appropriate placement while maintaining visibility across the transition.',
            slug: '',
            featureList: [
              'Post-acute placement',
              'Care transition visibility',
              'Transition performance insight',
              'Enterprise security posture for protected health information',
            ],
          }),
        ]}
      />
      <PublicNavbar />

      <Hero onDemo={openDemo} onPilot={openPilot} />
      <PatientJourney />
      <ProblemSection />
      <CloseTheTransition />
      <FourPillars />
      <VisibilityStatement />
      <HospitalROISection />
      <MultiSidedNetwork />
      <PilotSection onPilot={openPilot} onDemo={openDemo} />
      <TrustSection />
      <FinalCTA onDemo={openDemo} onPilot={openPilot} />

      <Footer />

      <InstitutionInquiryModal
        open={instModal.open}
        defaultInterest={instModal.interest}
        onOpenChange={(v) => setInstModal((s) => ({ ...s, open: v }))}
      />
    </div>
  );
}

/* ─────────────────────────────── Hero ──────────────────────────────── */

function Hero({ onDemo, onPilot }) {
  return (
    <section className="relative overflow-hidden" data-testid="hero">
      <div className="cn-container pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_.85fr] gap-12 lg:gap-16 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-bone-200/80 dark:bg-white/5 border border-cn-border mb-6 animate-fade-in-up"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cn-accent animate-pulse" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-cn-text">
                The Post-Acute Placement &amp; Care-Transition Network
              </span>
            </div>

            <h1
              className="cn-display text-[2.75rem] sm:text-6xl lg:text-7xl text-cn-text leading-[1.02] tracking-tight animate-fade-in-up stagger-1"
              data-testid="hero-headline"
            >
              Every discharge,<br />
              woven into{' '}
              <span className="italic font-medium text-cn-primary dark:text-cn-accent">one thread</span>{' '}
              of care.
            </h1>

            <p
              className="mt-6 text-xl md:text-2xl text-cn-text leading-snug max-w-xl animate-fade-in-up stagger-2"
              data-testid="hero-subheadline"
            >
              Move patients from hospital discharge to the right post-acute care&nbsp;— faster.
            </p>

            <p className="mt-5 text-base md:text-lg text-cn-text-muted leading-relaxed max-w-xl animate-fade-in-up stagger-2">
              CaraLoom is the connective layer between hospitals and post-acute care. From discharge
              planning through the start of care, the transition stays visible.
            </p>

            <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up stagger-3">
              <button
                type="button"
                onClick={onDemo}
                className="cn-btn cn-btn-primary text-base font-bold"
                data-testid="hero-cta-demo"
              >
                See CaraLoom in Action <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onPilot}
                className="cn-btn bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 text-base font-bold"
                data-testid="hero-cta-pilot"
              >
                Start a Hospital Pilot <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end animate-fade-in-up stagger-3">
            <HeroVisual />
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[480px] rounded-full bg-cn-sage-200/30 blur-3xl animate-cn-aurora" />
    </section>
  );
}

/* Static, outcome-only hero visual. Deliberately does NOT expose queues,
   scoring, matching factors, provider counts, timings, or any internal
   mechanism. Communicates only WHERE the transition ends up. */
function HeroVisual() {
  return (
    <div
      className="relative w-full max-w-[540px] rounded-3xl overflow-hidden border border-cn-border shadow-2xl bg-cn-primary text-white"
      data-testid="hero-visual"
    >
      <div className="absolute inset-0 opacity-25 pointer-events-none bg-gradient-to-br from-cn-accent/40 via-transparent to-cn-secondary/40" aria-hidden />
      <div className="relative p-8 md:p-10 aspect-[16/11] flex flex-col justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/70 font-mono">
            Transition · connected
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-cn-accent animate-pulse" aria-hidden />
            <span className="cn-display text-xl md:text-2xl text-white">On track</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="rounded-2xl bg-white/10 backdrop-blur-sm px-3 py-4 text-center border border-white/15">
            <Hospital className="w-5 h-5 mx-auto text-white/80" strokeWidth={1.6} />
            <div className="mt-2 text-[10px] uppercase tracking-widest text-white/70">Hospital</div>
          </div>
          <div className="rounded-2xl bg-cn-accent/90 text-cn-primary px-3 py-5 text-center border border-white/20 shadow-lg">
            <Sparkles className="w-5 h-5 mx-auto" strokeWidth={1.7} />
            <div className="mt-2 text-[10px] uppercase tracking-widest font-bold">CaraLoom</div>
          </div>
          <div className="rounded-2xl bg-white/10 backdrop-blur-sm px-3 py-4 text-center border border-white/15">
            <Stethoscope className="w-5 h-5 mx-auto text-white/80" strokeWidth={1.6} />
            <div className="mt-2 text-[10px] uppercase tracking-widest text-white/70">Post-acute</div>
          </div>
        </div>

        <div className="text-[11px] text-white/70 italic">
          Illustrative only. Detailed views shown in enterprise product demonstrations.
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Patient journey (the only public journey visual) ──
   Shows WHERE CaraLoom sits in healthcare, never HOW CaraLoom operates. */

function PatientJourney() {
  const stops = [
    {
      key: 'hospital',
      icon: Hospital,
      title: 'Hospital',
      body: 'Patient needs post-acute care.',
    },
    {
      key: 'caraloom',
      icon: Sparkles,
      title: 'CaraLoom',
      body: 'Intelligent post-acute coordination.',
      emphasized: true,
    },
    {
      key: 'post-acute',
      icon: Stethoscope,
      title: 'Post-Acute Care',
      body: 'The next level of care begins.',
    },
  ];
  return (
    <section
      id="journey"
      className="py-20 md:py-28 border-t border-cn-border bg-cn-bg"
      data-testid="patient-journey"
    >
      <div className="cn-container">
        <div className="max-w-3xl mx-auto text-center">
          <div className="cn-eyebrow mb-3">Where CaraLoom sits</div>
          <h2 className="cn-display text-3xl md:text-5xl text-cn-text leading-[1.06] tracking-tight">
            The connective layer between{' '}
            <span className="italic text-cn-text-muted">the hospital and post-acute care.</span>
          </h2>
        </div>

        {/* Desktop */}
        <div className="mt-14 hidden md:grid grid-cols-3 gap-6 items-stretch">
          {stops.map((s, i) => (
            <React.Fragment key={s.key}>
              <div
                className={`rounded-3xl border p-8 text-center flex flex-col items-center ${
                  s.emphasized
                    ? 'border-cn-primary/40 dark:border-cn-accent/60 bg-white dark:bg-cn-ink-800 shadow-lg'
                    : 'border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40'
                }`}
                data-testid={`journey-stop-${s.key}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  s.emphasized
                    ? 'bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent'
                    : 'bg-cn-bone-200 dark:bg-white/5 text-cn-text-muted'
                }`}>
                  <s.icon className="w-7 h-7" strokeWidth={1.6} />
                </div>
                <h3 className="mt-5 cn-display text-xl text-cn-text">{s.title}</h3>
                <p className="mt-2 text-sm text-cn-text-muted leading-relaxed max-w-[22ch]">{s.body}</p>
              </div>
              {i < stops.length - 1 && (
                <span aria-hidden className="hidden" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Mobile — vertical */}
        <ol className="mt-12 md:hidden space-y-4">
          {stops.map((s, i) => (
            <li key={s.key} data-testid={`journey-stop-mobile-${s.key}`}>
              <div
                className={`rounded-2xl border p-5 flex items-start gap-4 ${
                  s.emphasized
                    ? 'border-cn-primary/40 dark:border-cn-accent/60 bg-white dark:bg-cn-ink-800'
                    : 'border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40'
                }`}
              >
                <div className={`flex-none w-11 h-11 rounded-xl flex items-center justify-center ${
                  s.emphasized
                    ? 'bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent'
                    : 'bg-cn-bone-200 dark:bg-white/5 text-cn-text-muted'
                }`}>
                  <s.icon className="w-5 h-5" strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="cn-display text-base font-semibold text-cn-text">{s.title}</h3>
                  <p className="mt-1 text-sm text-cn-text-muted leading-relaxed">{s.body}</p>
                </div>
              </div>
              {i < stops.length - 1 && (
                <div className="flex justify-center py-2" aria-hidden>
                  <ArrowDown className="w-4 h-4 text-cn-text-muted opacity-50" />
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ───────────── Problem — "A referral isn't a placement." ─────────────
   Outcome-only. No internal sequence reveal. */

function ProblemSection() {
  return (
    <section
      className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border"
      data-testid="problem-section"
    >
      <div className="cn-container grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16">
        <div>
          <div className="cn-eyebrow mb-3">The gap</div>
          <h2 className="cn-display text-4xl md:text-6xl text-cn-text leading-[1.02] tracking-tight">
            A referral isn&apos;t{' '}
            <em className="not-italic italic text-cn-primary dark:text-cn-accent">a placement.</em>
          </h2>
          <p className="mt-6 text-lg text-cn-text-muted leading-relaxed max-w-xl">
            Hospitals have digitized referrals, but discharge teams still spend significant time
            searching for appropriate providers, checking capacity, following up on referrals and
            trying to determine what actually happened after the patient left.
          </p>
          <p className="mt-5 cn-display text-xl md:text-2xl text-cn-text max-w-xl">
            CaraLoom closes that gap.
          </p>
        </div>

        <div className="grid gap-3">
          {[
            'Referrals sent — but no confirmed acceptance.',
            'Discharge teams calling, faxing, and following up.',
            'The patient leaves without shared visibility.',
            'The transition ends where the referral ended.',
          ].map((line, i) => (
            <div
              key={i}
              className="rounded-2xl border border-cn-border bg-cn-bg/60 px-5 py-4 text-sm text-cn-text-muted leading-relaxed"
              data-testid={`problem-signal-${i}`}
            >
              {line}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Brand promise — Close the transition ─────────────── */

function CloseTheTransition() {
  return (
    <section
      className="py-24 md:py-32 bg-cn-bg"
      data-testid="close-the-transition"
    >
      <div className="cn-container max-w-4xl">
        <h2 className="cn-display text-4xl md:text-6xl text-cn-text leading-[1.04] tracking-tight">
          Don&apos;t just send the referral.{' '}
          <span className="italic text-cn-primary dark:text-cn-accent">Close the transition.</span>
        </h2>
        <p className="mt-6 text-lg text-cn-text-muted leading-relaxed max-w-2xl">
          CaraLoom keeps the transition connected from the moment a patient is ready for post-acute
          care until the receiving provider accepts the patient and care begins.
        </p>
      </div>
    </section>
  );
}

/* ─────────── Four independent pillars — NOT a workflow ────────────────
   MATCH · PLACE · CONNECT · MEASURE. Each is an outcome pillar. No
   numbering, no arrows, no "steps", no "process". These describe what
   the platform delivers — never how. */

function FourPillars() {
  return (
    <section
      className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border"
      data-testid="four-pillars"
    >
      <div className="cn-container">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">The platform</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            Four outcomes that characterize CaraLoom.
          </h2>
          <p className="mt-5 text-cn-text-muted leading-relaxed max-w-2xl">
            Not a process. Not a series of steps. Four capabilities that together define what
            hospitals get when they run their transitions on CaraLoom.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {PILLARS.map((p) => (
            <div
              key={p.key}
              className="rounded-2xl border border-cn-border bg-cn-bg p-6 lg:p-7"
              data-testid={`pillar-${p.key}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                <p.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <h3
                className="mt-6 cn-display text-xl text-cn-text tracking-tight"
                data-testid={`pillar-${p.key}-label`}
              >
                {p.label}
              </h3>
              <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Visibility statement (no dashboard mock) ───────────────
   Communicates the OUTCOME of transition visibility without exposing the
   internal statuses, queues, or scoring that produce it. */

function VisibilityStatement() {
  const outcomes = [
    { icon: CheckCircle2, label: 'Appropriate options' },
    { icon: CheckCircle2, label: 'Confirmed placement' },
    { icon: CheckCircle2, label: 'Coverage verified' },
    { icon: CheckCircle2, label: 'Start of care in sight' },
  ];
  return (
    <section
      className="py-24 md:py-32 bg-cn-bg"
      data-testid="transition-visibility"
    >
      <div className="cn-container">
        <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="cn-eyebrow mb-3">Transition visibility</div>
            <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.04] tracking-tight">
              Know where every discharge stands.
            </h2>
            <p className="mt-5 cn-display text-xl md:text-2xl text-cn-primary dark:text-cn-accent italic">
              Sent isn&apos;t enough.
            </p>
            <p className="mt-5 text-lg text-cn-text-muted leading-relaxed max-w-xl">
              From referral to first visit, the transition stays visible — so case-management
              leadership knows what&apos;s in flight, what&apos;s at risk, and what has landed.
            </p>
          </div>

          <div
            className="rounded-3xl border border-cn-border bg-cn-bone-100/70 dark:bg-cn-ink-900/50 p-6 md:p-8 shadow-sm"
            data-testid="visibility-outcome-card"
          >
            <div className="text-[11px] uppercase tracking-widest text-cn-text-muted">
              Transition · overview
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cn-accent animate-pulse" aria-hidden />
              <span className="cn-display text-xl text-cn-text">On track</span>
            </div>
            <ul className="mt-6 space-y-3">
              {outcomes.map((o) => (
                <li
                  key={o.label}
                  className="flex items-center gap-3"
                  data-testid={`visibility-outcome-${o.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <o.icon className="w-4 h-4 flex-none text-cn-primary dark:text-cn-accent" strokeWidth={2} />
                  <span className="text-sm text-cn-text">{o.label}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[11px] text-cn-text-muted italic">
              Illustrative outcomes only. Detailed views are shown in enterprise product
              demonstrations.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Hospital ROI (qualitative + optional verified metrics) ─── */

function HospitalROISection() {
  // Iter 179 · fetch verified pilot metrics; only render when published + non-empty.
  // Silent fallback to qualitative-only rendering on any error / no data.
  const [metrics, setMetrics] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/roi-metrics`);
        if (!alive || !data?.published) return;
        const byKey = {};
        (data.cards || []).forEach((c) => { if (c.value) byKey[c.key] = c; });
        setMetrics(byKey);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section
      className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border"
      data-testid="hospital-roi"
    >
      <div className="cn-container">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">For hospital leadership</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            Better transitions aren&apos;t just better care.{' '}
            <span className="italic text-cn-text-muted">They&apos;re better operations.</span>
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {HOSPITAL_ROI.map((r) => {
            const m = metrics[r.key];
            return (
              <div
                key={r.title}
                className="rounded-2xl border border-cn-border bg-cn-bg p-6"
                data-testid={`roi-card-${r.title.toLowerCase().replace(/ /g, '-')}`}
              >
                <div className="w-10 h-10 rounded-xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                  <r.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 cn-display text-lg text-cn-text">{r.title}</h3>
                <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{r.body}</p>
                {m && (
                  <div
                    className="mt-4 pt-4 border-t border-cn-border"
                    data-testid={`roi-card-metric-${r.key}`}
                  >
                    <div className="cn-display text-2xl text-cn-primary dark:text-cn-accent tabular-nums">{m.value}</div>
                    <div className="text-xs text-cn-text-muted">{m.caption}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-cn-text-muted italic max-w-2xl">
          Outcome metrics are reported from verified pilots — not published on this page until they
          reflect your actual results.
        </p>
      </div>
    </section>
  );
}

/* ─────────── Multi-sided network (Hospitals dominant) ────────────
   Shows WHO participates in the network, not HOW the network operates. */

function MultiSidedNetwork() {
  return (
    <section
      className="py-24 md:py-32 bg-cn-bg"
      data-testid="multi-sided-network"
    >
      <div className="cn-container">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">The network</div>
          <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
            One network.{' '}
            <span className="italic text-cn-text-muted">Every side of the transition.</span>
          </h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-[1.4fr_1fr_1fr] gap-6">
          <div
            className="rounded-3xl border-2 border-cn-primary/50 dark:border-cn-accent/60 bg-cn-primary text-white dark:bg-cn-accent/95 p-8 lg:p-10 shadow-lg"
            data-testid="network-hospitals"
          >
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
              <Hospital className="w-6 h-6" />
            </div>
            <h3 className="mt-6 cn-display text-2xl md:text-3xl leading-tight !text-white">For hospitals</h3>
            <p className="mt-4 text-base text-white/90 leading-relaxed max-w-lg">
              Place patients faster and maintain visibility through the transition.
            </p>
            <Link
              to="/discharge-planning-software"
              className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-cn-primary font-bold text-sm hover:bg-white/95"
              data-testid="network-hospitals-cta"
            >
              Explore for Hospitals <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div
            className="rounded-2xl border border-cn-border bg-cn-bg p-6"
            data-testid="network-providers"
          >
            <div className="w-10 h-10 rounded-xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
              <Stethoscope className="w-5 h-5" />
            </div>
            <h3 className="mt-5 cn-display text-lg text-cn-text">For post-acute providers</h3>
            <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">
              Participate in a connected network for post-acute care.
            </p>
            <Link
              to="/auth?role=agency"
              className="mt-6 inline-flex items-center gap-2 text-sm text-cn-primary dark:text-cn-accent font-semibold"
              data-testid="network-providers-cta"
            >
              Explore for Providers <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div
            className="rounded-2xl border border-cn-border bg-cn-bg p-6"
            data-testid="network-families"
          >
            <div className="w-10 h-10 rounded-xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="mt-5 cn-display text-lg text-cn-text">For patients &amp; families</h3>
            <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">
              Understand post-acute care options and stay connected during the transition.
            </p>
            <Link
              to="/auth?role=patient"
              className="mt-6 inline-flex items-center gap-2 text-sm text-cn-primary dark:text-cn-accent font-semibold"
              data-testid="network-families-cta"
            >
              Explore for Patients &amp; Families <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Pilot section ─────────── */

function PilotSection({ onPilot, onDemo }) {
  // Labels describe measurable domains, not internal metric definitions.
  const focusAreas = [
    'Placement outcomes',
    'Transition visibility',
    'Team time recovered',
    'Time to start of care',
  ];
  return (
    <section
      className="py-24 md:py-32 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border"
      data-testid="pilot-section"
    >
      <div className="cn-container">
        <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16 items-start">
          <div>
            <div className="cn-eyebrow mb-3">Hospital pilot</div>
            <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
              Start with one unit.{' '}
              <span className="italic text-cn-text-muted">Measure one transition.</span>
            </h2>
            <p className="mt-5 text-cn-text-muted leading-relaxed max-w-xl">
              Launch CaraLoom with a focused hospital pilot and measure what matters — for your
              service line, for your case-management team, and for your patients.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onPilot}
                className="cn-btn cn-btn-primary text-base font-bold"
                data-testid="pilot-cta-primary"
              >
                Start a Hospital Pilot <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onDemo}
                className="cn-btn bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 text-base"
                data-testid="pilot-cta-secondary"
              >
                Schedule a Demo <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {focusAreas.map((m) => (
              <li
                key={m}
                className="rounded-xl border border-cn-border bg-cn-bg px-4 py-3 flex items-center gap-3"
                data-testid={`pilot-focus-${m.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-cn-accent" aria-hidden />
                <span className="text-sm text-cn-text">{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Trust section ─────────── */

function TrustSection() {
  return (
    <section
      className="py-24 md:py-32 bg-cn-bg"
      data-testid="trust-section"
    >
      <div className="cn-container">
        <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-12 lg:gap-16 items-start">
          <div>
            <div className="cn-eyebrow mb-3">Enterprise readiness</div>
            <h2 className="cn-display text-4xl md:text-5xl text-cn-text leading-[1.05] tracking-tight">
              Built for the security review.
            </h2>
            <p className="mt-5 text-cn-text-muted leading-relaxed max-w-lg">
              Trust is more important than aggressive marketing language. CaraLoom is designed with
              HIPAA-aligned safeguards for protected health information, immutable audit trails, and
              role-scoped workflows. Certifications and interoperability integrations are declared
              only when they&apos;re actually in place.
            </p>
          </div>

          <ul className="space-y-6">
            {TRUST_PILLARS.map((t, i) => (
              <li key={t.title} className="flex gap-4 items-start" data-testid={`trust-pillar-${i}`}>
                <div className="flex-none w-11 h-11 rounded-full bg-cn-primary/10 dark:bg-cn-accent/10 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                  <t.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div>
                  <h4 className="cn-display text-base font-semibold text-cn-text leading-snug">{t.title}</h4>
                  <p className="text-sm text-cn-text-muted leading-relaxed mt-1">{t.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Final CTA ─────────── */

function FinalCTA({ onDemo, onPilot }) {
  return (
    <section
      id="cta"
      className="py-24 md:py-32 bg-cn-primary text-white"
      data-testid="final-cta"
    >
      <div className="cn-container text-center">
        <h2 className="cn-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.04] tracking-tight max-w-3xl mx-auto !text-white">
          The referral shouldn&apos;t be{' '}
          <em className="not-italic text-cn-accent">where visibility ends.</em>
        </h2>
        <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed max-w-xl mx-auto">
          See how CaraLoom can help your organization create a connected path from hospital discharge
          to post-acute care.
        </p>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={onDemo}
            className="cn-btn cn-btn-accent text-base font-bold"
            data-testid="final-cta-demo"
          >
            See CaraLoom in Action <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onPilot}
            className="cn-btn bg-white text-cn-primary hover:bg-white/95 text-base font-bold"
            data-testid="final-cta-pilot"
          >
            Start a Hospital Pilot <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
